import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CANCER_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";
import { legPainScreeningQuestions } from "./LegPainScreener";

/**
 * Variant B of Leg Pain screener
 * Splits Q12 (medicalConditions checkbox) into 7 individual yes/no radio questions
 * All other questions remain unchanged from the base screener.
 */

// Find the index of medicalConditions in the base screener
const medConditionsIndex = legPainScreeningQuestions.findIndex(q => q.id === "medicalConditions");

const splitExclusionQuestions: PreScreeningQuestion[] = [
  {
    id: "liver_failure",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with liver failure or cirrhosis?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_CIRRHOSIS, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "cancer_5_years",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with cancer in the past 5 years? (excluding SCC and BCC skin cancers)",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false }],
    }),
  },
  {
    id: "uncontrolled_hiv",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with HIV that is advanced or not well controlled?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false }],
    }),
  },
  {
    id: "hepatitis_b_c",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with active hepatitis B or hepatitis C?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "eskd_dialysis",
    type: QuestionType.RADIO,
    question: "Do you have end-stage kidney disease (ESKD) or are you currently receiving dialysis?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "cardiac_device",
    type: QuestionType.RADIO,
    question: "Do you have an implanted cardiac device (e.g., a pacemaker or defibrillator)?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PACEMAKER, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PACEMAKER, booleanAttributeValue: false }],
    }),
  },
  {
    id: "lower_limb_amputation",
    type: QuestionType.RADIO,
    question: "Have you had a lower limb amputation (excluding toe amputations)?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes", disqualify: true },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with this condition.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 1109, booleanAttributeValue: true }], // had_amputation_lowerlimb
      no: [{ attributeDefinitionId: 1109, booleanAttributeValue: false }],
    }),
  },
];

export const legPainScreeningQuestionsB: PreScreeningQuestion[] = [
  ...legPainScreeningQuestions.slice(0, medConditionsIndex), // Q1-Q11
  ...splitExclusionQuestions, // 7 individual radio questions (was Q12)
  ...legPainScreeningQuestions.slice(medConditionsIndex + 1), // Q13 onwards
];
